import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import { Provider } from 'react-redux';
import type { Store } from 'redux';
import { LoadingScreen } from './next/components/preload-screens';
import { MaintenanceBanner } from '../common/maintenance-banner';

// Inlined to avoid pulling map-websocket.ts (and its heavy import closure)
// into the eager boot path; that import previously perturbed bundle
// evaluation order and surfaced latent TDZ bugs elsewhere in the chunk.
const isPublicShareLink = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const hasMapId = /\/map\/next\/[0-9a-f-]+/.test(window.location.pathname);
  return Boolean(token && hasMapId);
};

/**
 * Map routes share a separate Redux store from the cloud app. Load it only when
 * this layout mounts so dashboard/auth pages do not pull MapLibre, deck.gl, MobX, etc.
 */
export const MapLayout = () => {
  const [mapStore, setMapStore] = useState<Store | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const [{ store }, { getInitialAuthState }] = await Promise.all([
        import('./redux'),
        import('./redux/authentication/reducer'),
      ]);
      if (cancelled) return;
      if (!isPublicShareLink()) {
        getInitialAuthState()(store.dispatch);
      }
      setMapStore(store);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!mapStore) {
    return <LoadingScreen />;
  }

  return (
    <Provider store={mapStore}>
      <div
        style={{
          width: '100%',
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          // Anchor absolutely-positioned map chrome (e.g. the bottom-centre
          // switcher pill) to the map area rather than the viewport, so it
          // stays centred when the assistant panel reserves the right column.
          position: 'relative',
        }}
      >
        <MaintenanceBanner />
        <Outlet />
      </div>
    </Provider>
  );
};
